/**
 * Field arrays and validation helpers for the fix-errors flow.
 *
 * "Optional" fields can be nulled out when the user skips them.
 * "Mandatory" fields for orders must all pass validation before submission.
 */

import type { AccessType } from '@/types/property.types';

// ---- Property optional fields (can be imported as null) -------------------

export const PROPERTY_OPTIONAL_FIELDS = [
  'unitNumber',
  'propertyId',
  'bedrooms',
  'bathrooms',
  'squareFeet',
  'unitId',
  'portfolioName',
  'propertyStatus',
  'rentReady',
  'listingDate',
  'isListed',
  'daysOnMarket',
  'availableDate',
  'leaseSignedDate',
  'leaseStartDate',
  'leaseEndDate',
  'moveInDate',
  'noticeGivenDate',
  'moveOutDate',
  'assignee',
  'isActive',
  'propId',
] as const;

// ---- Order optional fields ------------------------------------------------

export const ORDER_OPTIONAL_FIELDS = [
  'beds',
  'baths',
  'orderNotes',
  'gateCode',
  'firstAvailableDate',
  'propertyId',
  'previousOrderId',
  'squareFeet',
] as const;

// ---- Property mandatory fields --------------------------------------------

export const PROPERTY_MANDATORY_FIELDS = [
  'address',
  'city',
  'state',
  'zipCode',
  'propertyType',
  'entity',
] as const;

// ---- Order mandatory fields (used for inactive checks) --------------------

export const ORDER_MANDATORY_FIELDS = [
  'services',
  'propertyType',
  'address',
  'unitNumber',
  'city',
  'state',
  'zipCode',
  'onsiteContactName',
  'onsiteContactPhone',
  'accessType',
  'lockboxLocation',
  'accessCode',
  'rentlyRegistrationPin',
  'entity',
] as const;

// ---- Error cell shape -----------------------------------------------------

export interface ErrorCell {
  value: string | null;
  isActive: boolean;
  isDisabled?: boolean;
  isOptionalField?: boolean;
  errorMessage?: string;
}

export interface ErrorRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

// ---- Validation helpers ---------------------------------------------------

/**
 * Returns true when all mandatory fields for every item have isActive === true,
 * meaning every row has mandatory errors (nothing can be submitted).
 */
export const allRowsHaveMandatoryErrors = (
  data: ErrorRow[],
  mandatoryFields: readonly string[]
): boolean => {
  return data.every((item) =>
    mandatoryFields.some((field) => {
      const cell = item[field] as ErrorCell | undefined;
      return cell?.isActive === true;
    })
  );
};

/**
 * Returns true when any item has an isActive error in one of the given fields.
 */
export const hasActiveFieldInList = (
  data: ErrorRow[],
  fields: readonly string[]
): boolean => {
  return data.some((item) =>
    fields.some((field) => {
      const cell = item[field] as ErrorCell | undefined;
      return cell?.isActive === true;
    })
  );
};

/**
 * Returns true when ALL items have every field in `fields` with isActive false.
 */
export const allRowsFullyInactive = (
  data: ErrorRow[],
  fields: readonly string[]
): boolean => {
  return data.every((item) =>
    fields.every((field) => {
      const cell = item[field] as ErrorCell | undefined;
      return (
        cell !== undefined &&
        Object.prototype.hasOwnProperty.call(cell, 'isActive') &&
        cell.isActive === false
      );
    })
  );
};

/**
 * Returns true when at least ONE item has every field in `fields` with isActive false.
 */
export const anyRowFullyInactive = (
  data: ErrorRow[],
  fields: readonly string[]
): boolean => {
  return data.some((item) =>
    fields.every((field) => {
      const cell = item[field] as ErrorCell | undefined;
      return (
        cell !== undefined &&
        Object.prototype.hasOwnProperty.call(cell, 'isActive') &&
        cell.isActive === false
      );
    })
  );
};

/**
 * Access-type dependent value helpers: returns `null` when the access type
 * does not apply to the given field.
 */
export const getLockboxValue = (
  accessType: string | null,
  value: string | null
): string | null => {
  return accessType === 'Lockbox' ? value : null;
};

export const getAccessCodeValue = (
  accessType: string | null,
  value: string | null
): string | null => {
  return accessType === 'Lockbox' || accessType === 'Keypad' ? value : null;
};

export const getRentlyValue = (
  accessType: string | null,
  value: string | null
): string | null => {
  return accessType === 'Rently' ? value : null;
};

/**
 * Returns true when any field outside the optional list has isActive: true.
 * Used to determine whether optional fields should be nulled or kept.
 */
export const hasActiveFieldsExcludingOptional = (
  row: Record<string, ErrorCell | unknown>,
  optionalFields: readonly string[]
): boolean => {
  for (const field in row) {
    const cell = row[field] as ErrorCell | undefined;
    if (
      !optionalFields.includes(field) &&
      cell?.isActive === true
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Conditionally returns null when the mandate check or field check indicates
 * the value should be cleared.
 */
export const resolveNullableValue = (
  hasMandatoryErrors: boolean,
  fieldHasError: boolean,
  value: string | null
): string | null => {
  if (!hasMandatoryErrors) {
    if (fieldHasError) return null;
    return value === '-' ? null : value;
  }
  return value === '-' ? null : value;
};

/**
 * Normalise a dash-placeholder to null.
 */
export const dashToNull = (value: string | null | undefined): string | null => {
  if (value === undefined || value === '-') return null;
  return value;
};

/**
 * Convert MM/DD/YYYY or MM/DD/YY dates to normalised form.
 */
export const normaliseDateValue = (
  date: string | null | undefined
): string | null => {
  if (!date || !date.includes('/')) return date ?? null;
  const parts = date.split('/');
  if (parts[2] && parts[2].length > 2) {
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
  }
  return date;
};
